import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Brain, Users, Briefcase, ArrowRight, Globe, CheckCircle, Monitor } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const translations = {
  es: {
    hero: {
      title: 'Experiencia Virtual de Empleo en Empresa Digital',
      subtitle: 'Vive una experiencia laboral real en un entorno digital',
      description: 'Desarrolla habilidades prácticas, experimenta el día a día en una empresa y prepárate para tu próximo rol profesional.',
      cta: 'Comenzar ahora'
    },
    features: {
      title: 'Una experiencia laboral completa',
      list: [
        {
          title: 'Onboarding Virtual',
          description: 'Experimenta un proceso de incorporación realista con módulos de orientación y capacitación inicial.'
        },
        {
          title: 'Entorno Empresarial Virtual',
          description: 'Trabaja en un ambiente corporativo digital con todas las herramientas y sistemas necesarios.'
        },
        {
          title: 'Sistemas de Trabajo',
          description: 'Accede a CRM, ERP y herramientas de gestión de proyectos utilizadas en empresas reales.'
        },
        {
          title: 'Tareas del Mundo Real',
          description: 'Enfrenta desafíos y proyectos basados en situaciones laborales reales.'
        }
      ]
    },
    systems: {
      title: 'Sistemas y Herramientas',
      description: 'Accede a todas las herramientas que encontrarías en una empresa real',
      tools: [
        'Sistema CRM empresarial',
        'Software ERP integrado',
        'Herramientas de gestión de proyectos',
        'Plataforma de comunicación interna',
        'Sistema de tickets de soporte',
        'Portal de recursos humanos'
      ]
    }
  },
  en: {
    hero: {
      title: 'Digital Company & Virtual Employment Experience',
      subtitle: 'Experience real work in a digital environment',
      description: 'Develop practical skills, experience day-to-day company operations, and prepare for your next professional role.',
      cta: 'Get Started'
    },
    features: {
      title: 'A complete work experience',
      list: [
        {
          title: 'Virtual Onboarding',
          description: 'Experience a realistic onboarding process with orientation and initial training modules.'
        },
        {
          title: 'Virtual Business Environment',
          description: 'Work in a digital corporate environment with all necessary tools and systems.'
        },
        {
          title: 'Work Systems',
          description: 'Access CRM, ERP, and project management tools used in real companies.'
        },
        {
          title: 'Real-world Tasks',
          description: 'Face challenges and projects based on real work situations.'
        }
      ]
    },
    systems: {
      title: 'Systems & Tools',
      description: 'Access all the tools you would find in a real company',
      tools: [
        'Enterprise CRM system',
        'Integrated ERP software',
        'Project management tools',
        'Internal communication platform',
        'Support ticket system',
        'Human resources portal'
      ]
    }
  }
};

const LandingPage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-indigo-900/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight">
              {t.hero.title}
            </h1>
            <p className="mt-4 text-xl md:text-2xl text-foreground/80">
              {t.hero.subtitle}
            </p>
            <p className="mt-6 text-lg text-foreground/70 max-w-3xl mx-auto">
              {t.hero.description}
            </p>
            <div className="mt-10">
              <Link
                to="/dcvee/login"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-foreground">
              {t.features.title}
            </h2>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.features.list.map((feature, index) => (
              <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                <div className="flex items-center mb-4">
                  {index === 0 && <Users className="h-6 w-6 text-accent" />}
                  {index === 1 && <Building2 className="h-6 w-6 text-accent" />}
                  {index === 2 && <Monitor className="h-6 w-6 text-accent" />}
                  {index === 3 && <Briefcase className="h-6 w-6 text-accent" />}
                  <h3 className="ml-3 text-lg font-medium text-foreground">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-foreground/70">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Systems Section */}
      <div className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground">
              {t.systems.title}
            </h2>
            <p className="mt-4 text-lg text-foreground/70">
              {t.systems.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.systems.tools.map((tool, index) => (
              <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-1" />
                  <span className="ml-3 text-foreground">{tool}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;